/**
 * Veroni PDP gallery — fix #1 (variant-filtered gallery), tightened per owner
 * directive: "Amethyst + Rose Gold must show ONLY amethyst-rose-gold jewelry —
 * no photos of any other metal color or gemstone."
 *
 * Renders ALL product media in the DOM once (see blocks/veroni-pdp-gallery.liquid).
 * This script hides/shows individual <li data-media-id> items using a strict,
 * ordered filter against data-media-map ({stone, color} per media id, parsed
 * server side primarily from each image's CDN filename — see the Liquid block
 * for the parse chain). Exact string equality only, never substring — a prior
 * bidirectional-substring version of this filter let e.g. a selected "Pink
 * Sapphire" match a plain "Sapphire" image, which is exactly the kind of
 * cross-stone leak the owner directive rules out.
 *
 * Strict tier order for the effective selected stone S + Gold Color C — NEVER
 * a different (explicitly-tagged) stone, NEVER a different (explicitly-tagged)
 * color, NO fallback-to-all (owner report, 2026-07-14, fix #4 — clasp/closure
 * images that carry a color token but no stone token):
 *   1. stone === S AND color === C             (exact)
 *   2. stone === S AND color === ''             (stone-only: neutral-color shot of that stone)
 *   3. stone === '' AND color === C             (color-only: e.g. a clasp image tagged
 *                                                 by gold color but no stone — shown only
 *                                                 when that color is selected, any stone)
 *   4. stone === '' AND color === ''            (fully neutral: clasp/chain diagrams — always last)
 * Only if tiers 1-4 are ALL empty does it fall back to any-color media tagged
 * with the same stone (stone === S regardless of color), logging console.warn
 * so a missing exact combo is visible without ever leaving the gallery blank.
 * The full visible set is reordered into tier order (not just the featured
 * image) so both the desktop grid and the mobile carousel read in that order.
 *
 * Fix #2 (owner report, 2026-07-14): products with exactly one Gemstone value
 * (or, defensively, exactly one Gold Color value) render NO radios for that
 * option at all — snippets/variant-main-picker.liquid's fix #3 skips
 * single-value option fieldsets entirely, since there's nothing to choose.
 * selectedOptionValue() then reads that axis as '' (no checked input found),
 * but the media map still tags every image with that one real stone/color
 * name (parsed from its filename) — a strict '' === mStone comparison would
 * match nothing and blank the whole gallery. computeVisibleIds() detects this
 * by scanning the media map itself: when an axis carries zero or exactly one
 * distinct non-blank value across this product's media, that axis has
 * nothing to *select* between, so its one real value (or '' if genuinely
 * none) is used as the effective selection instead of the un-set '' from the
 * DOM — wildcarding the axis rather than leaving it unmatched. This reuses
 * the exact same 4-tier logic above unchanged; it only changes what counts
 * as "selected" going in.
 *
 * Decoupled from Horizon's internal variant-picker web component / AJAX
 * morph system on purpose: this listens for native `change` events on the
 * option radio inputs (event bubbling fires those synchronously before any
 * async morph), plus a MutationObserver as a safety net for AJAX-replaced
 * variant-picker markup. Selection state itself is always read fresh from
 * the DOM's checked radios, so it never gets out of sync.
 */
(function () {
  var GEMSTONE_OPTION_NAMES = ['gemstone', 'colored stone', 'stone', 'initial'];
  var COLOR_OPTION_NAMES = ['gold color', 'color', 'metal'];

  function normalize(str) {
    return (str || '').trim().toLowerCase();
  }

  /** Gold Color option values are "Yellow Gold" / "White Gold" / "Rose Gold";
   * media-map color tokens (parsed server side against a bare word list) are
   * just "yellow" / "white" / "rose". Strip the " gold" suffix so both sides
   * can be compared with exact equality instead of substring matching. */
  function normalizeColor(str) {
    return normalize(str).replace(/\s*gold\s*$/, '').trim();
  }

  /**
   * Catalog gemstone option values are often "Ruby & Diamond" or
   * "Diamond & Ruby" (accent diamonds). Media alts/maps store only the
   * colored stone ("ruby"). Strip the diamond-accent half so both sides
   * compare. Leave true two-stone combos ("Citrine & Garnet") intact —
   * stonesEquivalent() matches those against either part.
   */
  function normalizeStone(str) {
    var stone = normalize(str);
    if (!stone) return '';
    stone = stone
      .replace(/^(?:lab[-\s]?grown\s+)/, '')
      .replace(/^(?:diamond\s*(?:&|and|\+|\/)\s*)/, '')
      .replace(/\s*(?:&|and|\+|\/)\s*diamonds?$/, '')
      .replace(/\s*\([^)]*\)\s*$/, '')
      .trim();
    if (/^multi(?:-|\s)?(?:gemstone|stone)$/.test(stone)) return 'multi-gemstone';
    return stone;
  }

  /** Exact stone match, or a combo option ("citrine & garnet") vs one part. */
  function stonesEquivalent(selected, media) {
    var sel = normalizeStone(selected);
    var med = normalizeStone(media);
    if (!sel || !med) return false;
    if (sel === med) return true;
    if (/[&/]|\band\b/.test(sel)) {
      var parts = sel.split(/\s*(?:&|and|\+|\/)\s*/);
      for (var i = 0; i < parts.length; i++) {
        if (normalizeStone(parts[i]) === med) return true;
      }
    }
    return false;
  }

  function mediaStem(src) {
    var fn = ((src || '').split('?')[0].split('/').pop() || '').toLowerCase();
    fn = fn.replace(/\.[a-z0-9]+$/, '');
    return fn.replace(/_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, '');
  }

  var NEUTRAL_FILE_RE = /(?:^|[-_])(clasp|closure|packaging|diagram)(?:[-_.]|$)/;

  function identityFromFilename(src) {
    var fn = normalize((src || '').split('?')[0].split('/').pop());
    if (!fn) return null;
    if (!NEUTRAL_FILE_RE.test(fn)) return null;
    var color = '';
    if (/(?:^|[-_])yellow(?:[-_.]|$)/.test(fn)) color = 'yellow';
    else if (/(?:^|[-_])rose(?:[-_.]|$)/.test(fn)) color = 'rose';
    else if (/(?:^|[-_])white(?:[-_.]|$)/.test(fn)) color = 'white';
    return { stone: '', color: color, neutral: true };
  }

  /**
   * Corrected Shopify media alt text is the catalog source of truth. Some
   * migrated CDN filenames still contain the gemstone name that originally
   * occupied the same array position, so re-derive identity from the rendered
   * image alt before filtering. The server map remains the fallback for old
   * media without a useful alt (including initial-letter products).
   */
  function identityFromAlt(alt) {
    var text = normalize(alt);
    if (!text) return null;
    // Longest-first. Keep identical to `stone_words` in the Liquid block.
    var stones = [
      'pink sapphire', 'pink topaz', 'green sapphire', 'green amethyst',
      'blue topaz', 'white topaz', 'black pearl', 'lemon quartz',
      'rose quartz', 'smoky quartz', 'cubic zirconia', 'multi-gemstone',
      'multi gemstone', 'multi-stone', 'multi stone', 'alexandrite',
      'tsavorite', 'amethyst', 'sapphire', 'emerald', 'aquamarine', 'citrine',
      'garnet', 'peridot', 'tanzanite', 'zirconia', 'diamond', 'onyx', 'opal',
      'pearl', 'morganite', 'tourmaline', 'spinel', 'ruby'
    ];
    var stone = '';
    for (var i = 0; i < stones.length; i++) {
      if (text.indexOf(stones[i]) !== -1) {
        stone = stones[i];
        break;
      }
    }
    // Gold color is a prefix contract: SEO wording after the first two words
    // is descriptive only and must never change variant/gallery identity.
    var colorMatch = text.match(/^(yellow|white|rose)\s+gold\b/);
    var color = colorMatch ? colorMatch[1] : '';
    return stone || color ? { stone: stone, color: color } : null;
  }

  /** Find the currently checked value for an option matched by name aliases. */
  function selectedOptionValue(root, aliasList) {
    var fieldsets = root.querySelectorAll('fieldset.variant-option, .variant-option');
    for (var i = 0; i < fieldsets.length; i++) {
      var fieldset = fieldsets[i];
      var legend = fieldset.querySelector('legend');
      var legendText = '';
      if (legend) {
        // legend's first text node holds the option name; ignore any nested
        // .variant-option__swatch-value span with the selected value text.
        var firstNode = legend.childNodes[0];
        legendText = normalize(firstNode && firstNode.textContent);
      }
      if (aliasList.indexOf(legendText) === -1) continue;
      var current = fieldset.querySelector('input[type="radio"][data-current-checked="true"]');
      if (current) return normalize(current.value);
      var checkedList = fieldset.querySelectorAll('input[type="radio"]:checked');
      if (checkedList.length === 1) return normalize(checkedList[0].value);
      if (checkedList.length > 1) {
        return normalize(checkedList[checkedList.length - 1].value);
      }
      var select = fieldset.querySelector('select');
      if (select) return normalize(select.value);
    }
    // Dropdown style options render outside <fieldset>, as
    // .variant-option--dropdowns with a <label> + <select>.
    var dropdowns = root.querySelectorAll('.variant-option--dropdowns');
    for (var j = 0; j < dropdowns.length; j++) {
      var wrap = dropdowns[j];
      var label = wrap.querySelector('label');
      if (!label || aliasList.indexOf(normalize(label.textContent)) === -1) continue;
      var sel = wrap.querySelector('select');
      if (sel) return normalize(sel.value);
    }
    return '';
  }

  /** Keep variant-sensitive materials copy from naming the first/default stone. */
  function syncDynamicGemstoneCopy(stone) {
    if (!stone) return;
    var displayStone = stone.replace(/\b\w/g, function (letter) {
      return letter.toUpperCase();
    });
    var bodies = document.querySelectorAll(
      '.v-buybox-specs__body, .v-accordion__panel--rte'
    );
    bodies.forEach(function (body) {
      var walker = document.createTreeWalker(body, NodeFilter.SHOW_TEXT);
      var node;
      while ((node = walker.nextNode())) {
        node.nodeValue = node.nodeValue.replace(
          /The featured stone shown here is lab-grown [^,.;]+(?:,\s*[\d.]+\s*ct total weight)?\./gi,
          'The selected stone is lab-grown ' + displayStone + '.'
        ).replace(
          /The selected stone is lab-grown [^.]+\./gi,
          'The selected stone is lab-grown ' + displayStone + '.'
        );
      }
    });
  }

  function VeroniGallery(el) {
    this.el = el;
    this.mediaMap = {};
    try {
      this.mediaMap = JSON.parse(el.dataset.mediaMap || '{}');
    } catch (e) {
      this.mediaMap = {};
    }
    this.items = Array.prototype.slice.call(el.querySelectorAll('.v-gallery__item'));
    var seenStems = {};
    this.items.forEach(function (item) {
      var id = item.getAttribute('data-media-id');
      var img = item.querySelector('img');
      if (!id) return;
      var src = (img && (img.getAttribute('src') || img.getAttribute('srcset') || '')) || '';
      if (!item.hasAttribute('data-always-last')) {
        var stem = mediaStem(src);
        if (stem) {
          if (seenStems[stem]) {
            item.setAttribute('data-duplicate-media', '');
            item.setAttribute('hidden', '');
            item.style.display = 'none';
            return;
          }
          seenStems[stem] = true;
        }
      }
      var current = this.mediaMap[id] || { stone: '', color: '' };
      var fileId = identityFromFilename(src);
      if (fileId && fileId.neutral) {
        this.mediaMap[id] = { stone: '', color: fileId.color || current.color || '' };
        return;
      }
      var corrected = identityFromAlt(img && img.getAttribute('alt'));
      if (!corrected) return;
      this.mediaMap[id] = {
        stone: normalizeStone(corrected.stone || current.stone || ''),
        color: corrected.color || current.color || ''
      };
    }, this);
    this.dots = Array.prototype.slice.call(el.querySelectorAll('.v-gallery__dot'));
    this.thumbs = Array.prototype.slice.call(el.querySelectorAll('.v-gallery__thumb'));
    this.grid = el.querySelector('.v-gallery__grid');
    this.zoomTriggers = Array.prototype.slice.call(el.querySelectorAll('[data-zoom-trigger]'));
    this.zoomDialog = el.querySelector('[data-zoom-dialog]');
    this.zoomImage = el.querySelector('[data-zoom-image]');
    this.zoomCount = el.querySelector('[data-zoom-count]');
    this.zoomIndex = 0;

    this.filter = this.filter.bind(this);
    this.setActiveDotFromScroll = this.setActiveDotFromScroll.bind(this);
    this.showZoomAt = this.showZoomAt.bind(this);
    this.closeZoom = this.closeZoom.bind(this);
    this.goToSlide = this.goToSlide.bind(this);
    this.visibleItems = this.visibleItems.bind(this);
    this.isDesktopCarousel = this.isDesktopCarousel.bind(this);
    this.ensureDesktopFrame = this.ensureDesktopFrame.bind(this);
    this.slideIndex = 0;
    this.zoomScale = 1;
    this.zoomX = 0;
    this.zoomY = 0;

    this.ensureDesktopFrame();
    this.filter();
    this.bindEvents();
    this.bindZoomGestures();
  }

  VeroniGallery.prototype.isDesktopCarousel = function () {
    return typeof window.matchMedia === 'function' && window.matchMedia('(min-width: 750px)').matches;
  };

  /** Visible (not filtered-out) gallery items in DOM order. */
  VeroniGallery.prototype.visibleItems = function () {
    return this.items.filter(function (it) {
      return !it.hasAttribute('hidden') && it.style.display !== 'none';
    });
  };

  /**
   * Desktop Mejuri-style: wrap the slide list in a fixed-aspect frame so
   * only one image shows; transform moves between slides on dot click.
   */
  VeroniGallery.prototype.ensureDesktopFrame = function () {
    if (!this.grid || !this.el) return;
    if (this.frame) return;
    var frame = document.createElement('div');
    frame.className = 'v-gallery__frame';
    this.grid.parentNode.insertBefore(frame, this.grid);
    frame.appendChild(this.grid);
    this.frame = frame;
  };

  VeroniGallery.prototype.goToSlide = function (index, opts) {
    var visible = this.visibleItems();
    if (!visible.length) return;
    var next = ((index % visible.length) + visible.length) % visible.length;
    this.slideIndex = next;

    if (this.isDesktopCarousel() && this.grid) {
      var y = -next * 100;
      this.grid.style.transform = 'translate3d(0, ' + y + '%, 0)';
    } else if (this.grid) {
      var item = visible[next];
      if (item) {
        item.scrollIntoView({
          behavior: opts && opts.instant ? 'auto' : 'smooth',
          inline: 'start',
          block: 'nearest',
        });
      }
    }

    var activeId = visible[next].getAttribute('data-media-id');
    this.dots.forEach(function (dot) {
      var on = dot.getAttribute('data-dot-for') === activeId;
      dot.setAttribute('aria-selected', on ? 'true' : 'false');
      dot.classList.toggle('is-active', on);
    });
    this.thumbs.forEach(function (thumb) {
      var on = thumb.getAttribute('data-thumb-for') === activeId;
      thumb.setAttribute('aria-selected', on ? 'true' : 'false');
      thumb.classList.toggle('is-active', on);
      if (on) thumb.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
    });
  };

  VeroniGallery.prototype.bindEvents = function () {
    var self = this;

    // Native radio/select change events bubble up from the variant-picker.
    document.addEventListener('change', function (event) {
      var target = event.target;
      if (!target) return;
      if (target.matches('input[type="radio"], select')) {
        // Defer a tick so a currently-updating checked state settles.
        window.setTimeout(self.filter, 0);
      }
    });

    // Safety net: Horizon's variant-picker can AJAX-morph its own markup
    // (e.g. combined listings). Re-run the filter if that subtree changes.
    var variantPicker = document.querySelector('variant-picker');
    if (variantPicker && 'MutationObserver' in window) {
      var observer = new MutationObserver(function () {
        window.setTimeout(self.filter, 0);
      });
      observer.observe(variantPicker, { childList: true, subtree: true, attributes: true, attributeFilter: ['checked'] });
    }

    // Dots: click jumps to that slide (desktop transform / mobile scroll).
    if (this.dots.length && this.grid) {
      this.dots.forEach(function (dot) {
        dot.addEventListener('click', function (event) {
          event.preventDefault();
          var id = dot.getAttribute('data-dot-for');
          var visible = self.visibleItems();
          var idx = -1;
          for (var i = 0; i < visible.length; i++) {
            if (visible[i].getAttribute('data-media-id') === id) {
              idx = i;
              break;
            }
          }
          if (idx >= 0) self.goToSlide(idx);
        });
      });
      this.grid.addEventListener('scroll', debounce(this.setActiveDotFromScroll, 100), { passive: true });
    }

    // Thumbnail rail: same slide navigation as the dots, with a larger visual target.
    if (this.thumbs.length) {
      this.thumbs.forEach(function (thumb) {
        thumb.addEventListener('click', function (event) {
          event.preventDefault();
          var id = thumb.getAttribute('data-thumb-for');
          var visible = self.visibleItems();
          for (var i = 0; i < visible.length; i++) {
            if (visible[i].getAttribute('data-media-id') === id) {
              self.goToSlide(i);
              break;
            }
          }
        });
      });
    }

    window.addEventListener(
      'resize',
      debounce(function () {
        self.ensureDesktopFrame();
        self.goToSlide(self.slideIndex, { instant: true });
      }, 150)
    );

    // Click-to-enlarge viewer. Native <dialog> supplies focus trapping and
    // Escape-to-close; ArrowLeft/ArrowRight move through only the images
    // currently visible for the selected gemstone + gold color.
    if (this.zoomDialog && this.zoomImage && this.zoomTriggers.length) {
      this.zoomTriggers.forEach(function (trigger) {
        trigger.addEventListener('click', function () {
          var visible = self.visibleZoomTriggers();
          var index = visible.indexOf(trigger);
          if (index === -1) return;
          self.zoomIndex = index;
          self.showZoomAt(index);
          if (typeof self.zoomDialog.showModal === 'function' && !self.zoomDialog.open) {
            self.zoomDialog.showModal();
          }
        });
      });

      var closeButton = this.zoomDialog.querySelector('[data-zoom-close]');
      var previousButton = this.zoomDialog.querySelector('[data-zoom-previous]');
      var nextButton = this.zoomDialog.querySelector('[data-zoom-next]');

      if (closeButton) {
        closeButton.addEventListener('click', function (event) {
          event.stopPropagation();
          self.closeZoom();
        });
      }
      if (previousButton) {
        previousButton.addEventListener('click', function (event) {
          event.preventDefault();
          event.stopPropagation();
          self.showZoomAt(self.zoomIndex - 1);
        });
      }
      if (nextButton) {
        nextButton.addEventListener('click', function (event) {
          event.preventDefault();
          event.stopPropagation();
          self.showZoomAt(self.zoomIndex + 1);
        });
      }

      this.zoomDialog.addEventListener('click', function (event) {
        // Only close when clicking backdrop/stage chrome — not nav/image/controls
        if (
          event.target === self.zoomDialog ||
          event.target.classList.contains('v-gallery-zoom__stage')
        ) {
          self.closeZoom();
        }
      });
      this.zoomDialog.addEventListener('keydown', function (event) {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          self.showZoomAt(self.zoomIndex - 1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          self.showZoomAt(self.zoomIndex + 1);
        }
      });
    }
  };

  VeroniGallery.prototype.visibleZoomTriggers = function () {
    return this.zoomTriggers.filter(function (trigger) {
      var item = trigger.closest('.v-gallery__item');
      return item && !item.hidden && item.style.display !== 'none';
    });
  };

  VeroniGallery.prototype.resetZoomTransform = function () {
    this.zoomScale = 1;
    this.zoomX = 0;
    this.zoomY = 0;
    this.applyZoomTransform();
  };

  VeroniGallery.prototype.applyZoomTransform = function () {
    if (!this.zoomImage) return;
    var scale = this.zoomScale || 1;
    var x = this.zoomX || 0;
    var y = this.zoomY || 0;
    this.zoomImage.style.transform = 'translate3d(' + x + 'px,' + y + 'px,0) scale(' + scale + ')';
    this.zoomImage.style.cursor = scale > 1.02 ? 'grab' : 'zoom-in';
    if (this.zoomDialog) {
      this.zoomDialog.classList.toggle('is-zoomed', scale > 1.02);
    }
  };

  VeroniGallery.prototype.showZoomAt = function (requestedIndex) {
    var visible = this.visibleZoomTriggers();
    if (!visible.length || !this.zoomImage) return;

    var index = (requestedIndex + visible.length) % visible.length;
    var trigger = visible[index];
    var src = trigger.getAttribute('data-zoom-src') || '';
    var alt = trigger.getAttribute('data-zoom-alt') || '';
    this.zoomIndex = index;
    this.resetZoomTransform();
    // Fade briefly so the swap doesn't flash hard between images.
    this.zoomImage.classList.add('is-loading');
    this.zoomImage.onload = function () {
      this.classList.remove('is-loading');
    };
    this.zoomImage.src = src;
    this.zoomImage.alt = alt;

    // Preload neighbors so arrow navigation feels instant
    if (visible.length > 1) {
      var nextIdx = (index + 1) % visible.length;
      var prevIdx = (index - 1 + visible.length) % visible.length;
      [nextIdx, prevIdx].forEach(function (i) {
        var s = visible[i] && visible[i].getAttribute('data-zoom-src');
        if (!s) return;
        var pre = new Image();
        pre.src = s;
      });
    }

    if (this.zoomCount) {
      this.zoomCount.textContent = 'Image ' + (index + 1) + ' of ' + visible.length;
    }

    var previousButton = this.zoomDialog && this.zoomDialog.querySelector('[data-zoom-previous]');
    var nextButton = this.zoomDialog && this.zoomDialog.querySelector('[data-zoom-next]');
    if (previousButton) previousButton.hidden = visible.length < 2;
    if (nextButton) nextButton.hidden = visible.length < 2;
  };

  VeroniGallery.prototype.closeZoom = function () {
    this.resetZoomTransform();
    if (this.zoomDialog && this.zoomDialog.open) this.zoomDialog.close();
  };

  VeroniGallery.prototype.bindZoomGestures = function () {
    var self = this;
    if (!this.zoomDialog || !this.zoomImage || this._zoomGesturesBound) return;
    this._zoomGesturesBound = true;
    this.zoomScale = 1;
    this.zoomX = 0;
    this.zoomY = 0;
    var dragging = false;
    var lastX = 0;
    var lastY = 0;
    var pinchStartDist = 0;
    var pinchStartScale = 1;

    function distance(t1, t2) {
      var dx = t1.clientX - t2.clientX;
      var dy = t1.clientY - t2.clientY;
      return Math.sqrt(dx * dx + dy * dy);
    }

    // Double-click / double-tap to toggle 2× zoom centered on the point.
    this.zoomImage.addEventListener('dblclick', function (event) {
      event.preventDefault();
      if ((self.zoomScale || 1) > 1.05) {
        self.resetZoomTransform();
        return;
      }
      var rect = self.zoomImage.getBoundingClientRect();
      var cx = event.clientX - (rect.left + rect.width / 2);
      var cy = event.clientY - (rect.top + rect.height / 2);
      self.zoomScale = 2.25;
      self.zoomX = -cx * 0.55;
      self.zoomY = -cy * 0.55;
      self.applyZoomTransform();
    });

    // Wheel zoom (desktop trackpad / mouse).
    this.zoomDialog.addEventListener(
      'wheel',
      function (event) {
        if (!self.zoomDialog.open) return;
        event.preventDefault();
        var delta = event.deltaY > 0 ? -0.12 : 0.12;
        var next = Math.min(4, Math.max(1, (self.zoomScale || 1) + delta));
        if (next === 1) {
          self.resetZoomTransform();
          return;
        }
        self.zoomScale = next;
        self.applyZoomTransform();
      },
      { passive: false }
    );

    this.zoomImage.addEventListener('pointerdown', function (event) {
      if ((self.zoomScale || 1) <= 1.02) return;
      dragging = true;
      lastX = event.clientX;
      lastY = event.clientY;
      self.zoomImage.setPointerCapture(event.pointerId);
      self.zoomImage.style.cursor = 'grabbing';
    });

    this.zoomImage.addEventListener('pointermove', function (event) {
      if (!dragging) return;
      var dx = event.clientX - lastX;
      var dy = event.clientY - lastY;
      lastX = event.clientX;
      lastY = event.clientY;
      self.zoomX = (self.zoomX || 0) + dx;
      self.zoomY = (self.zoomY || 0) + dy;
      self.applyZoomTransform();
    });

    function endDrag(event) {
      if (!dragging) return;
      dragging = false;
      try {
        self.zoomImage.releasePointerCapture(event.pointerId);
      } catch (e) {}
      self.applyZoomTransform();
    }
    this.zoomImage.addEventListener('pointerup', endDrag);
    this.zoomImage.addEventListener('pointercancel', endDrag);

    // Pinch-to-zoom on touch.
    this.zoomDialog.addEventListener(
      'touchstart',
      function (event) {
        if (event.touches.length === 2) {
          pinchStartDist = distance(event.touches[0], event.touches[1]);
          pinchStartScale = self.zoomScale || 1;
        }
      },
      { passive: true }
    );
    this.zoomDialog.addEventListener(
      'touchmove',
      function (event) {
        if (event.touches.length !== 2 || !pinchStartDist) return;
        event.preventDefault();
        var dist = distance(event.touches[0], event.touches[1]);
        var next = Math.min(4, Math.max(1, pinchStartScale * (dist / pinchStartDist)));
        self.zoomScale = next;
        if (next <= 1.02) {
          self.resetZoomTransform();
        } else {
          self.applyZoomTransform();
        }
      },
      { passive: false }
    );
    this.zoomDialog.addEventListener('touchend', function () {
      pinchStartDist = 0;
    });
  };

  VeroniGallery.prototype.setActiveDotFromScroll = function () {
    if (!this.grid) return;
    // Desktop uses transform carousel — index is owned by goToSlide.
    if (this.isDesktopCarousel()) return;
    var gridRect = this.grid.getBoundingClientRect();
    var visible = this.items
      .filter(function (it) {
        return it.style.display !== 'none' && !it.hasAttribute('hidden');
      })
      .reduce(function (closest, it) {
        var rect = it.getBoundingClientRect();
        var dist = Math.abs(rect.left - gridRect.left);
        if (!closest || dist < closest.dist) return { item: it, dist: dist };
        return closest;
      }, null);
    if (!visible) return;
    var id = visible.item.getAttribute('data-media-id');
    var self = this;
    var idx = this.visibleItems().findIndex
      ? this.visibleItems().findIndex(function (it) {
          return it.getAttribute('data-media-id') === id;
        })
      : -1;
    // findIndex polyfill-free path
    if (idx < 0) {
      var vis = this.visibleItems();
      for (var i = 0; i < vis.length; i++) {
        if (vis[i].getAttribute('data-media-id') === id) {
          idx = i;
          break;
        }
      }
    }
    if (idx >= 0) this.slideIndex = idx;
    this.dots.forEach(function (dot) {
      var on = dot.getAttribute('data-dot-for') === id;
      dot.setAttribute('aria-selected', on ? 'true' : 'false');
      dot.classList.toggle('is-active', on);
    });
  };

  /**
   * Collect the distinct non-blank values of mediaMap[id][key] across ids.
   * Used to detect a "single value / no value" axis for the fix #2 wildcard.
   */
  function collectDistinct(mediaMap, ids, key) {
    var seen = {};
    var out = [];
    ids.forEach(function (id) {
      var meta = mediaMap[id] || {};
      var v = meta[key] || '';
      if (v && !seen[v]) {
        seen[v] = true;
        out.push(v);
      }
    });
    return out;
  }

  /**
   * Fix #2: if `selected` is already set (a real DOM selection), use it
   * as-is. Otherwise — no radios rendered for this axis at all — check
   * whether the media map itself carries zero or exactly one distinct
   * non-blank value for that axis. If so, there was never anything to
   * choose between, so wildcard the axis to that one real value (or ''
   * if the product truly has none) instead of leaving it as an
   * unmatchable ''. If the media map somehow carries >1 distinct value
   * with no way to select between them, leave it as '' (unchanged,
   * conservative — matches only the fully-neutral/color-only tiers).
   */
  function resolveEffective(mediaMap, ids, key, selected) {
    if (selected) return selected;
    var distinct = collectDistinct(mediaMap, ids, key);
    return distinct.length <= 1 ? distinct[0] || '' : '';
  }

  /**
   * Pure, standalone-testable core: given the media map and the selected
   * {stone, color}, returns the ordered list of media ids to show plus
   * whether the empty-case fallback (and its console.warn) fired. Kept
   * side-effect-free (no DOM) so it can be extracted and unit-tested.
   *
   * Fix #4 adds a third "color-only" tier (media tagged with a color but no
   * stone — e.g. a clasp/closure image whose filename carries a gold-color
   * token but no gemstone token) between the existing stone-only and
   * fully-neutral tiers. Fix #2 wildcards the stone and/or color axis first
   * (see resolveEffective) when a product has no radios for that option at
   * all, so the same tier logic below is reused unmodified for both the
   * normal multi-value case and the single-value case.
   */
  function computeVisibleIds(mediaMap, ids, stone, color) {
    var effectiveStone = resolveEffective(mediaMap, ids, 'stone', stone);
    var effectiveColor = resolveEffective(mediaMap, ids, 'color', color);

    var exact = []; // tier 1: stone === S && color === C
    var stoneOnly = []; // tier 2: stone === S && color === ''
    var colorOnly = []; // tier 3: stone === '' && color === C (fix #4)
    var neutral = []; // tier 4: stone === '' && color === '' (always last)
    var sameStoneAnyColor = []; // fallback pool if tiers 1-4 are all empty

    ids.forEach(function (id) {
      var meta = mediaMap[id] || { stone: '', color: '' };
      var mStone = meta.stone || '';
      var mColor = meta.color || '';
      var stoneTagged = mStone !== '';
      var colorTagged = mColor !== '';
      var stoneMatches = stoneTagged && effectiveStone !== '' && stonesEquivalent(effectiveStone, mStone);
      var colorMatches = colorTagged && effectiveColor !== '' && mColor === effectiveColor;

      if (stoneTagged && !stoneMatches) {
        // tagged with a DIFFERENT stone than the one in play — never shown,
        // no cross-stone leakage, no fallback-to-all.
        return;
      }

      if (stoneMatches) {
        sameStoneAnyColor.push(id);
        if (colorMatches) {
          exact.push(id);
        } else if (!colorTagged) {
          stoneOnly.push(id);
        }
        // else: same stone but a DIFFERENT specific color — never shown.
      } else if (!stoneTagged && colorTagged && colorMatches) {
        // fix #4: color-only media (no stone token at all) — shown only
        // when that gold color is selected, regardless of stone.
        colorOnly.push(id);
      } else if (!stoneTagged && !colorTagged) {
        neutral.push(id);
      }
      // else: no stone tag + a color tag that doesn't match — never shown.
    });

    var matchedIds = exact.concat(stoneOnly).concat(colorOnly).concat(neutral);
    var usedFallback = false;

    if (matchedIds.length === 0) {
      usedFallback = true;
      matchedIds = sameStoneAnyColor; // may itself be empty — gallery stays
      // empty only if there is truly zero media for this stone at all.
      // eslint-disable-next-line no-console
      console.warn('veroni: no exact media for', stone, color);
    }

    return { matchedIds: matchedIds, usedFallback: usedFallback };
  }

  VeroniGallery.prototype.filter = function () {
    var stone = normalizeStone(selectedOptionValue(document, GEMSTONE_OPTION_NAMES));
    var color = normalizeColor(selectedOptionValue(document, COLOR_OPTION_NAMES));
    syncDynamicGemstoneCopy(stone);
    var mediaMap = this.mediaMap;
    var grid = this.grid;

    var idToItem = {};
    var alwaysLastIds = [];
    var ids = [];
    this.items.forEach(function (it) {
      var id = it.getAttribute('data-media-id');
      idToItem[id] = it;
      if (it.hasAttribute('data-duplicate-media')) {
        it.setAttribute('hidden', '');
        it.style.display = 'none';
        return;
      }
      if (it.hasAttribute('data-always-last')) {
        alwaysLastIds.push(id);
      } else {
        ids.push(id);
      }
    });

    // Theme-level brand/packaging frames are not product media and must not
    // influence stone/color wildcard detection or the missing-combination
    // fallback. Filter real product media first, then append global frames.
    var result = ids.length
      ? computeVisibleIds(mediaMap, ids, stone, color)
      : { matchedIds: [], usedFallback: false };
    var matchedIds = result.matchedIds.concat(alwaysLastIds);
    var visibleSet = {};
    matchedIds.forEach(function (id) {
      visibleSet[id] = true;
    });

    this.items.forEach(function (item) {
      var id = item.getAttribute('data-media-id');
      if (visibleSet[id]) {
        item.removeAttribute('hidden');
        item.style.removeProperty('display');
      } else {
        item.setAttribute('hidden', '');
        item.style.display = 'none';
      }
    });

    // Reorder the visible items into strict tier order (exact, then
    // stone-neutral, then fully-neutral) — not just the featured image —
    // so both the 2-col grid's reading order and the mobile scroll-snap
    // carousel's slide order follow the required priority.
    if (grid) {
      matchedIds.forEach(function (id) {
        var item = idToItem[id];
        if (item) grid.appendChild(item);
      });
      grid.scrollLeft = 0;
    }

    // Reorder dots to match slide order; hide filtered-out dots.
    var self = this;
    if (this.dots.length && this.el) {
      var dotsHost = this.el.querySelector('.v-gallery__dots');
      matchedIds.forEach(function (id) {
        var dot = self.dots.filter(function (d) {
          return d.getAttribute('data-dot-for') === id;
        })[0];
        if (dot && dotsHost) {
          dot.hidden = false;
          dotsHost.appendChild(dot);
        }
      });
      this.dots.forEach(function (dot) {
        var id = dot.getAttribute('data-dot-for');
        if (!visibleSet[id]) {
          dot.hidden = true;
          dot.setAttribute('aria-selected', 'false');
          dot.classList.remove('is-active');
        }
      });
    }

    // Keep thumbnails in the same filtered order as slides and dots.
    if (this.thumbs.length) {
      var thumbsHost = this.el.querySelector('.v-gallery__thumbs');
      matchedIds.forEach(function (id) {
        var thumb = self.thumbs.filter(function (candidate) {
          return candidate.getAttribute('data-thumb-for') === id;
        })[0];
        if (thumb && thumbsHost) {
          thumb.hidden = false;
          thumbsHost.appendChild(thumb);
        }
      });
      this.thumbs.forEach(function (thumb) {
        var id = thumb.getAttribute('data-thumb-for');
        if (!visibleSet[id]) {
          thumb.hidden = true;
          thumb.setAttribute('aria-selected', 'false');
          thumb.classList.remove('is-active');
        }
      });
      if (thumbsHost) thumbsHost.hidden = matchedIds.length <= 1;
    }

    // Fix #5: single-image state (no dead gap; hide dots when only one).
    this.el.classList.toggle('v-gallery--single', matchedIds.length === 1);
    if (this.el.querySelector('.v-gallery__dots')) {
      this.el.querySelector('.v-gallery__dots').style.display =
        matchedIds.length <= 1 ? 'none' : '';
    }

    // Land on first slide of the new filtered set.
    this.slideIndex = 0;
    if (this.grid && this.isDesktopCarousel()) {
      this.grid.style.transform = 'translate3d(0, 0, 0)';
    }
    this.goToSlide(0, { instant: true });
  };

  // Exposed for standalone/unit testing (node) without touching the DOM.
  VeroniGallery.computeVisibleIds = computeVisibleIds;

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments;
      var ctx = this;
      window.clearTimeout(t);
      t = window.setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  /**
   * Shopify can briefly serve a cached full product document after a theme
   * block changes even though its section renderer is already current. Keep
   * the Rosetta editorial prototype visible during that window. The Liquid
   * block remains authoritative; this exits immediately when that markup is
   * present, so there can never be a duplicate note.
   */
  function ensureRosettaEditorialPrototype() {
    if (!/^\/products\/rosetta-earrings-zara-collection\/?$/.test(window.location.pathname)) return;
    if (document.querySelector('.v-gallery-editorial')) return;

    var gallery = document.querySelector('media-gallery-veroni');
    if (!gallery) return;

    var note = document.createElement('aside');
    note.className = 'v-gallery-editorial';
    note.setAttribute('aria-label', 'Product style note');
    note.innerHTML =
      '<span class="v-gallery-editorial__rule" aria-hidden="true"></span>' +
      '<p>A modern chandelier with a light, rhythmic swing. Rosetta layers five bright gemstones in graduated drops, balancing statement scale with an open setting that keeps the pair easy to wear.</p>';
    gallery.insertAdjacentElement('afterend', note);
    gallery.classList.add('v-gallery--has-editorial');

    if (!document.getElementById('veroni-editorial-prototype-style')) {
      var style = document.createElement('style');
      style.id = 'veroni-editorial-prototype-style';
      style.textContent =
        '.v-gallery-editorial{width:min(100%,640px);margin:clamp(18px,2.5vw,36px) 0 clamp(56px,7vw,96px);padding-inline:clamp(20px,4vw,56px);box-sizing:border-box}' +
        '@media screen and (min-width:750px){.v-gallery.v-gallery--has-editorial{position:relative;top:auto}}' +
        '.v-gallery-editorial__rule{display:block;width:44px;height:1px;margin-block-end:20px;background:var(--veroni-gold,#c2a06b)}' +
        '.v-gallery-editorial p{max-width:36ch;margin:0;color:var(--veroni-ink,#211e1a);font-family:var(--font-secondary--family,Georgia,serif);font-size:clamp(18px,1.75vw,24px);font-weight:400;line-height:1.48;letter-spacing:-.015em;text-wrap:pretty}' +
        '@media screen and (max-width:749px){.v-gallery-editorial{margin-block:16px 52px;padding-inline:20px}.v-gallery-editorial p{font-size:18px}}';
      document.head.appendChild(style);
    }
  }

  function init() {
    document.querySelectorAll('media-gallery-veroni').forEach(function (el) {
      if (el.veroniGalleryInstance) return;
      el.veroniGalleryInstance = new VeroniGallery(el);
    });
    ensureRosettaEditorialPrototype();
  }

  // Guarded so this file can also be `require()`d standalone in node (no DOM)
  // to unit-test computeVisibleIds — see the CommonJS export above/below.
  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }

    // Re-init if a quick-add modal / section re-render injects a new gallery.
    if (typeof window !== 'undefined' && 'MutationObserver' in window) {
      new MutationObserver(function (mutations) {
        var shouldInit = false;
        mutations.forEach(function (m) {
          m.addedNodes.forEach(function (node) {
            if (node.nodeType === 1 && (node.matches && node.matches('media-gallery-veroni') || node.querySelector && node.querySelector('media-gallery-veroni'))) {
              shouldInit = true;
            }
          });
        });
        if (shouldInit) init();
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { computeVisibleIds: computeVisibleIds };
  }
})();

// dot-carousel-v1
